import { chartColor } from '@/shared/lib/chart-theme';
import { cn } from '@/shared/lib/cn';
import { useFacilityScope } from '@/shared/lib/hooks/use-facility-scope';
import { formatNumber } from '@/shared/lib/number';
import { DonutChart, Loader, TooltipElement, Typography } from '@/shared/ui';
import { useQuery } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import { type FC } from 'react';
import { getOverallOccupancyQueryOptions } from '../api/get-overall-occupancy.query';

interface OverallOccupancyCardProps {
  dateRange: {
    from: Dayjs;
    to: Dayjs;
  };
}

export const OverallOccupancyCard: FC<OverallOccupancyCardProps> = ({
  dateRange,
}) => {
  const { scopedFacilityId } = useFacilityScope();
  const { data, isLoading } = useQuery(
    getOverallOccupancyQueryOptions({
      from: dateRange.from.format('YYYY-MM-DD'),
      to: dateRange.to.format('YYYY-MM-DD'),
      facilitiesIds: scopedFacilityId ? [scopedFacilityId] : undefined,
    })
  );

  if (!isLoading && !data) {
    return (
      <div className="border-neutral-5 flex w-[187px] flex-col items-center gap-2 border bg-white p-3">
        <Typography variant="h5" weight="medium" className="pb-3">
          Overall Occupancy
        </Typography>

        <Typography className="flex flex-1 items-center">No data</Typography>
      </div>
    );
  }

  const currentAvg = data?.occupancy.currentAvg ?? 0;
  const changePct = data?.occupancy.changePct ?? 0;

  const occupiedBeds = data?.occupiedBeds;
  const vacantBeds = data?.vacantBeds;

  const totalBeds =
    occupiedBeds != null && vacantBeds != null
      ? occupiedBeds + vacantBeds
      : null;

  const vacantPct = 100 - currentAvg;

  let changeColor = chartColor('--color-neutral-3');
  let changeColorHover = 'gray';

  if (changePct !== null) {
    changeColor = changePct < 0 ? '#FF7875' : '#73D13D';
    changeColorHover = changePct < 0 ? 'red' : 'green';
  }

  const isPositiveChange = changePct !== null && changePct >= 0;

  const chartData = {
    labels: [
      vacantBeds ? 'Vacant Beds' : 'N/A',
      changePct ? 'Diff from prev period' : 'N/A',
      occupiedBeds ? 'Occupied Beds' : 'N/A',
    ],
    datasets: [
      {
        data: [vacantPct, changePct, currentAvg],
        backgroundColor: [
          chartColor('--color-neutral-3'),
          changeColor,
          chartColor('--color-primary-5'),
        ],
        hoverBackgroundColor: ['gray', changeColorHover, 'blue'],
        borderWidth: 0,
      },
    ],
  };

  const innerContent = (
    <div className="text-center">
      <div>
        <Typography
          className={cn(
            `text-sm`,
            changePct
              ? changePct >= 0
                ? 'text-polar-green-7'
                : 'text-dust-red-5'
              : 'text-neutral-8'
          )}
        >
          {changePct ? (changePct >= 0 ? '+' : '') : ''}
          {changePct === null ? 'N/A' : (changePct ?? 0).toFixed(1) + '%'}
        </Typography>
      </div>
      <Typography weight="medium" className="text-2xl">
        {currentAvg === null ? 'N/A' : (currentAvg ?? 0).toFixed(1) + '%'}
      </Typography>
    </div>
  );

  const linkedPair: [number, number] | undefined =
    changePct === null ? undefined : isPositiveChange ? [1, 2] : [1, 0];

  return (
    <div className="border-neutral-5 flex w-[200px] flex-col items-center gap-2 border bg-white p-3">
      <div className="flex items-center gap-x-2 pb-3">
        <Typography variant="h5" weight="medium">
          Overall Occupancy
        </Typography>
        <TooltipElement content="Average number of occupied beds divided by the average number of licensed beds" />
      </div>

      <div className="mt-auto mb-auto px-3">
        {isLoading ? (
          <Loader />
        ) : (
          <DonutChart
            className="flex h-[138px] w-[138px] items-center justify-center"
            data={chartData}
            linkedPair={linkedPair}
            innerContent={innerContent}
            shouldLabelShowPercents={true}
          />
        )}
      </div>
      {!isLoading && (
        <div className="flex items-center gap-2">
          <Typography className="text-neutral-8" variant="footnote-description">
            {`Vacant ${vacantBeds != null ? formatNumber(vacantBeds) : 'N/A'} of ${totalBeds != null ? formatNumber(totalBeds) : 'N/A'} beds`}
          </Typography>

          <TooltipElement content="Number of vacant beds as of the end of the filtered period" />
        </div>
      )}
    </div>
  );
};
